import * as React from "react";

import { cn } from "@/lib/utils";

export interface AuroraProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "light" | "dark";
}

/**
 * Decorative background. Each blob is promoted once via `will-change:
 * transform` and drifts with translation only — blur radius is kept at 24px
 * because rasterisation cost grows roughly with the square of the radius, and
 * the gradient stops are softened to compensate visually.
 */
export function Aurora({ variant = "light", className, ...props }: AuroraProps) {
  const dark = variant === "dark";
  const promote = { willChange: "transform" } as const;

  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden",
        dark && "bg-ink-900",
        className,
      )}
      {...props}
    >
      <div
        style={promote}
        className={cn(
          "laa-blob-a absolute -left-[12%] -top-[32%] h-[72%] w-[62%] blur-[24px]",
          dark
            ? "bg-[radial-gradient(closest-side,oklch(0.7845_0.1325_181.91_/_0.22),oklch(0.7845_0.1325_181.91_/_0.07)_55%,transparent_82%)]"
            : "bg-[radial-gradient(closest-side,oklch(0.7845_0.1325_181.91_/_0.42),oklch(0.7845_0.1325_181.91_/_0.14)_55%,transparent_82%)]",
        )}
      />
      <div
        style={promote}
        className={cn(
          "laa-blob-b absolute -right-[14%] top-[2%] h-[68%] w-[58%] blur-[24px]",
          dark
            ? "bg-[radial-gradient(closest-side,oklch(0.6056_0.2189_292.72_/_0.16),oklch(0.6056_0.2189_292.72_/_0.05)_55%,transparent_82%)]"
            : "bg-[radial-gradient(closest-side,oklch(0.6056_0.2189_292.72_/_0.32),oklch(0.6056_0.2189_292.72_/_0.10)_55%,transparent_82%)]",
        )}
      />
      <div
        style={promote}
        className={cn(
          "laa-blob-c absolute bottom-[-36%] left-[22%] h-[64%] w-[56%] blur-[24px]",
          dark
            ? "bg-[radial-gradient(closest-side,oklch(0.6324_0.1980_259.75_/_0.12),oklch(0.6324_0.1980_259.75_/_0.04)_55%,transparent_82%)]"
            : "bg-[radial-gradient(closest-side,oklch(0.6324_0.1980_259.75_/_0.22),oklch(0.6324_0.1980_259.75_/_0.07)_55%,transparent_82%)]",
        )}
      />
    </div>
  );
}

export default Aurora;
